import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApi, formatApiError } from './adminApi';
import { ArrowLeft, Save, Eye, EyeOff, AlertCircle, CheckCircle2 } from 'lucide-react';
import ImageUploader from './ImageUploader';

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const EMPTY = {
  slug: '',
  title: '',
  excerpt: '',
  body: '',
  category: 'Brand Strategy',
  image: '/generated/blog_performance.png',
  date: '',
  readTime: '6 min read',
  author: 'Grouth Editorial',
  metaDescription: '',
  published: true,
  publishedAt: '',
  tags: [],
};

const Field = ({ label, hint, children, required }) => (
  <div className="mb-5">
    <label className="block text-[11px] uppercase tracking-[0.15em] text-white/50 font-['Inter'] mb-2">
      {label}
      {required && <span className="text-[#83BF42] ml-1">*</span>}
    </label>
    {children}
    {hint && <p className="text-white/30 text-xs font-['Lato'] mt-1.5">{hint}</p>}
  </div>
);

const AdminArticleEditor = () => {
  const { slug: routeSlug } = useParams();
  const isNew = !routeSlug || routeSlug === 'new';
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY);
  const [originalSlug, setOriginalSlug] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [preview, setPreview] = useState(false);
  const [touched, setTouched] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [views, setViews] = useState(0);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    adminApi
      .listArticles()
      .then((all) => {
        const found = all.find((a) => a.slug === routeSlug);
        if (!found) {
          setError('Article not found.');
        } else {
          setForm({
            slug: found.slug,
            title: found.title,
            excerpt: found.excerpt,
            body: found.body,
            category: found.category,
            image: found.image,
            date: found.date,
            readTime: found.readTime,
            author: found.author,
            metaDescription: found.metaDescription || '',
            published: found.published !== false,
            publishedAt: found.publishedAt
              ? new Date(found.publishedAt).toISOString().slice(0, 16)
              : '',
            tags: Array.isArray(found.tags) ? found.tags : [],
          });
          setOriginalSlug(found.slug);
          setViews(found.views || 0);
        }
      })
      .finally(() => setLoading(false));
  }, [isNew, routeSlug]);

  // Auto-slug when creating a new article
  useEffect(() => {
    if (isNew && !touched && form.title) {
      setForm((f) => ({ ...f, slug: slugify(f.title) }));
    }
  }, [form.title, isNew, touched]);

  const wordCount = useMemo(() => {
    return form.body
      ? form.body.trim().split(/\s+/).filter(Boolean).length
      : 0;
  }, [form.body]);

  const estimatedReadTime = useMemo(() => {
    if (wordCount === 0) return '';
    const minutes = Math.max(1, Math.round(wordCount / 220));
    return `${minutes} min read`;
  }, [wordCount]);

  const handleChange = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const payload = { ...form };
      // Auto-fill missing optionals
      if (!payload.readTime || payload.readTime === '6 min read') {
        if (estimatedReadTime) payload.readTime = estimatedReadTime;
      }
      if (!payload.metaDescription) payload.metaDescription = payload.excerpt;
      if (!payload.date) {
        payload.date = new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' });
      }
      // Convert datetime-local string to ISO; empty means null (publish now)
      if (payload.publishedAt) {
        payload.publishedAt = new Date(payload.publishedAt).toISOString();
      } else {
        payload.publishedAt = null;
      }

      if (isNew) {
        const created = await adminApi.createArticle(payload);
        setSuccess('Article created.');
        setOriginalSlug(created.slug);
        setTimeout(() => navigate(`/admin/articles/${created.slug}`, { replace: true }), 800);
      } else {
        const updated = await adminApi.updateArticle(originalSlug, payload);
        setSuccess('Article saved.');
        setOriginalSlug(updated.slug);
        if (updated.slug !== routeSlug) {
          setTimeout(() => navigate(`/admin/articles/${updated.slug}`, { replace: true }), 800);
        }
      }
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#83BF42] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="px-8 lg:px-12 py-10 max-w-6xl" data-testid="admin-article-editor">
      <button
        onClick={() => navigate('/admin/articles')}
        data-testid="editor-back"
        className="inline-flex items-center gap-2 text-white/50 text-sm font-['Inter'] mb-6 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to articles
      </button>

      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <p className="text-[#83BF42] text-xs uppercase tracking-[0.2em] font-['Inter'] mb-2">
            {isNew ? 'New article' : 'Edit article'}
          </p>
          <h1 className="text-white text-3xl font-light font-['Inter']">
            {isNew ? 'Write a new insight' : form.title || 'Untitled'}
          </h1>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setPreview((p) => !p)}
            data-testid="editor-toggle-preview"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/10 text-white/80 text-sm font-['Inter'] hover:bg-white/5 transition"
          >
            {preview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {preview ? 'Edit' : 'Preview'}
          </button>
          <button
            form="article-form"
            type="submit"
            disabled={saving}
            data-testid="editor-save"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#83BF42] text-[#0F0F11] text-sm font-medium font-['Inter'] hover:bg-[#A8D96C] transition disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving…' : isNew ? 'Publish' : 'Save changes'}
          </button>
        </div>
      </header>

      {error && (
        <div data-testid="editor-error" className="flex items-start gap-2 mb-5 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
        </div>
      )}
      {success && (
        <div data-testid="editor-success" className="flex items-start gap-2 mb-5 px-4 py-3 rounded-lg bg-[#83BF42]/10 border border-[#83BF42]/20 text-[#83BF42] text-sm">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> {success}
        </div>
      )}

      {preview ? (
        <ArticlePreview article={form} />
      ) : (
        <form id="article-form" onSubmit={handleSubmit} className="bg-[#15151A] border border-white/5 rounded-2xl p-6 lg:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Field label="Title" required>
                <input
                  required
                  value={form.title}
                  onChange={handleChange('title')}
                  data-testid="editor-title"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-lg font-['Inter'] focus:outline-none focus:border-[#83BF42] transition-colors"
                  placeholder="Why most performance marketing is a waste of money"
                />
              </Field>

              <Field label="Slug" required hint={`URL: /insights/${form.slug || 'your-slug'}`}>
                <input
                  required
                  value={form.slug}
                  onChange={(e) => {
                    setTouched(true);
                    setForm({ ...form, slug: slugify(e.target.value) });
                  }}
                  data-testid="editor-slug"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white font-mono text-sm focus:outline-none focus:border-[#83BF42] transition-colors"
                  placeholder="why-most-performance-marketing-is-a-waste"
                />
              </Field>

              <Field label="Excerpt" required hint="The hook shown on listings and as the article lead.">
                <textarea
                  required
                  rows={2}
                  value={form.excerpt}
                  onChange={handleChange('excerpt')}
                  data-testid="editor-excerpt"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white font-['Lato'] text-sm focus:outline-none focus:border-[#83BF42] transition-colors resize-none"
                />
              </Field>

              <Field
                label="Body"
                required
                hint={`${wordCount} words${estimatedReadTime ? ` · ~${estimatedReadTime}` : ''}. Separate paragraphs with a blank line.`}
              >
                <textarea
                  required
                  rows={18}
                  value={form.body}
                  onChange={handleChange('body')}
                  data-testid="editor-body"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white font-['Lato'] text-[15px] leading-relaxed focus:outline-none focus:border-[#83BF42] transition-colors resize-y"
                />
              </Field>
            </div>

            <aside className="lg:col-span-1 space-y-1">
              <Field label="Category" required>
                <input
                  required
                  value={form.category}
                  onChange={handleChange('category')}
                  data-testid="editor-category"
                  list="article-categories"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white font-['Inter'] text-sm focus:outline-none focus:border-[#83BF42] transition-colors"
                />
                <datalist id="article-categories">
                  {['Brand Strategy', 'Market Insights', 'Adtech', 'User Acquisition', 'Digital Marketing'].map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </Field>

              <Field label="Cover image" hint="Path or absolute URL.">
                <ImageUploader
                  value={form.image}
                  onChange={(url) => setForm({ ...form, image: url })}
                  testId="editor-image-uploader"
                />
              </Field>

              <Field label="Read time">
                <input
                  value={form.readTime}
                  onChange={handleChange('readTime')}
                  placeholder={estimatedReadTime || '6 min read'}
                  data-testid="editor-readtime"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white font-['Inter'] text-sm focus:outline-none focus:border-[#83BF42] transition-colors"
                />
              </Field>

              <Field label="Display date" hint='e.g. "March 2025". Defaults to current month.'>
                <input
                  value={form.date}
                  onChange={handleChange('date')}
                  data-testid="editor-date"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white font-['Inter'] text-sm focus:outline-none focus:border-[#83BF42] transition-colors"
                />
              </Field>

              <Field label="Author">
                <input
                  value={form.author}
                  onChange={handleChange('author')}
                  data-testid="editor-author"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white font-['Inter'] text-sm focus:outline-none focus:border-[#83BF42] transition-colors"
                />
              </Field>

              <Field label="Meta description" hint="Used for SEO and social previews. Defaults to excerpt.">
                <textarea
                  rows={3}
                  value={form.metaDescription}
                  onChange={handleChange('metaDescription')}
                  data-testid="editor-meta"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white font-['Lato'] text-xs focus:outline-none focus:border-[#83BF42] transition-colors resize-none"
                />
              </Field>

              <Field label="Status" hint="Drafts are hidden from /insights.">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, published: true })}
                    data-testid="editor-published"
                    className={`flex-1 px-4 py-2.5 rounded-lg text-xs font-['Inter'] border transition-all ${
                      form.published
                        ? 'bg-[#83BF42]/15 text-[#83BF42] border-[#83BF42]/30'
                        : 'bg-white/[0.03] text-white/40 border-white/10 hover:text-white'
                    }`}
                  >
                    Published
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, published: false })}
                    data-testid="editor-draft"
                    className={`flex-1 px-4 py-2.5 rounded-lg text-xs font-['Inter'] border transition-all ${
                      !form.published
                        ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                        : 'bg-white/[0.03] text-white/40 border-white/10 hover:text-white'
                    }`}
                  >
                    Draft
                  </button>
                </div>
              </Field>

              <Field label="Schedule (optional)" hint="If set in the future, the article stays hidden from /insights until that time.">
                <input
                  type="datetime-local"
                  value={form.publishedAt || ''}
                  onChange={handleChange('publishedAt')}
                  data-testid="editor-scheduled-at"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-['Inter'] focus:outline-none focus:border-[#83BF42] transition-colors"
                />
              </Field>

              <Field label="Tags" hint="Press Enter or comma to add. Used by readers to filter related insights.">
                <div className="rounded-lg bg-white/5 border border-white/10 px-2 py-2 focus-within:border-[#83BF42] transition-colors" data-testid="editor-tags-wrap">
                  <div className="flex flex-wrap gap-2 items-center">
                    {form.tags.map((t) => (
                      <span
                        key={t}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#83BF42]/15 text-[#83BF42] text-xs font-['Inter']"
                        data-testid={`editor-tag-${t.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        #{t}
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, tags: form.tags.filter((x) => x !== t) })}
                          aria-label={`Remove ${t}`}
                          className="text-[#83BF42]/70 hover:text-white transition-colors"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    <input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
                          e.preventDefault();
                          const next = tagInput.trim().replace(/^#/, '').slice(0, 32);
                          if (next && !form.tags.includes(next) && form.tags.length < 8) {
                            setForm({ ...form, tags: [...form.tags, next] });
                          }
                          setTagInput('');
                        } else if (e.key === 'Backspace' && !tagInput && form.tags.length) {
                          setForm({ ...form, tags: form.tags.slice(0, -1) });
                        }
                      }}
                      onBlur={() => {
                        if (tagInput.trim()) {
                          const next = tagInput.trim().replace(/^#/, '').slice(0, 32);
                          if (next && !form.tags.includes(next) && form.tags.length < 8) {
                            setForm({ ...form, tags: [...form.tags, next] });
                          }
                          setTagInput('');
                        }
                      }}
                      data-testid="editor-tag-input"
                      placeholder={form.tags.length ? '' : 'attribution, africa, fintech…'}
                      className="flex-1 min-w-[120px] bg-transparent outline-none text-white text-sm font-['Lato'] placeholder-white/30"
                    />
                  </div>
                </div>
              </Field>

              {!isNew && (
                <div className="mt-4 px-4 py-3 rounded-lg bg-white/[0.03] border border-white/5" data-testid="editor-stats">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-white/40 font-['Inter']">Stats</p>
                  <p className="text-white text-2xl font-light font-['Inter'] mt-1">
                    {views.toLocaleString()}
                  </p>
                  <p className="text-white/40 text-xs font-['Lato']">
                    public reads tracked
                  </p>
                </div>
              )}
            </aside>
          </div>
        </form>
      )}
    </div>
  );
};

const ArticlePreview = ({ article }) => {
  const paragraphs = (article.body || '').split('\n\n').filter((p) => p.trim());
  return (
    <div className="bg-white text-[#242326] rounded-2xl overflow-hidden" data-testid="editor-preview">
      <div className="relative h-[280px]">
        {article.image && <img src={article.image} alt={article.title} className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <div className="absolute bottom-6 left-6 right-6">
          <span className="text-[#83BF42] text-xs uppercase tracking-wider font-bold font-['Inter']">
            {article.category}
          </span>
          <h1 className="text-white text-2xl md:text-4xl font-light font-['Inter'] leading-tight mt-2">
            {article.title || 'Untitled'}
          </h1>
        </div>
      </div>
      <div className="p-8">
        <p className="text-[#242326] text-lg font-['Inter'] font-light leading-relaxed pb-6 mb-6 border-b border-[#242326]/10">
          {article.excerpt}
        </p>
        <div className="space-y-5">
          {paragraphs.map((p, i) => (
            <p key={i} className="text-[#4A4A48] text-[15px] font-['Lato'] leading-[1.8]">
              {p}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminArticleEditor;
